import { Sheet, useBreakpointValue } from '@hover/blueprint';
import { assertUnreachable } from '@hover/utilities';
import React, { useState } from 'react';
import { useHistory } from 'react-router';

import type { HomeownerAppMenuNamesValues } from 'src/modules/homeownerApp/providers/HomeownerAppProvider';
import { HomeownerAppMenuNames } from 'src/modules/homeownerApp/providers/HomeownerAppProvider';

import { useHomeownerAppContext } from '../../hooks/useHomeownerAppContext';
import { useSaveToProjectContext } from '../../providers/SaveToProjectProvider';
import { MoreActions } from '../OriginalImageScreen/MoreActions';
import { ProfileSheet } from '../ProfileSheet';
import { AddIdeasToProject } from '../Projects/Menu/AddIdeasToProject';
import { AddSavedToProject } from '../Projects/Menu/AddSavedToProject';
import { AddToProject } from '../Projects/Menu/AddToProject';
import { EditProject } from '../Projects/Menu/EditProject';
import { ProjectMoreActions } from '../Projects/Menu/MoreActions';
import { NewProject } from '../Projects/Menu/NewProject';
import { WelcomeSheet } from '../WelcomeSheet';

import { SignInSheet } from './SignInSheet';
import { UploadPhotoSheet } from './UploadPhotoSheet';

const bottomVariantSheetMenus: HomeownerAppMenuNamesValues[] = [
  HomeownerAppMenuNames.UPLOAD_PHOTO,
  HomeownerAppMenuNames.ADD_IDEAS_TO_PROJECT,
  HomeownerAppMenuNames.ADD_SAVED_TO_PROJECT,
];

const bottomVariantSheetMenusSet = new Set(bottomVariantSheetMenus);

const mobileMenusWithCloseButton: HomeownerAppMenuNamesValues[] = [
  HomeownerAppMenuNames.ADD_TO_PROJECT,
  HomeownerAppMenuNames.UPLOAD_PHOTO,
  HomeownerAppMenuNames.ORIGINAL_IMAGE_MORE_ACTIONS,
  HomeownerAppMenuNames.PROFILE,
  HomeownerAppMenuNames.SIGN_IN,
  HomeownerAppMenuNames.WELCOME_TO,
  HomeownerAppMenuNames.WELCOME_BACK,
  HomeownerAppMenuNames.ADD_SAVED_TO_PROJECT,
  HomeownerAppMenuNames.ADD_IDEAS_TO_PROJECT,
];

const mobileMenusWithCloseButtonSet = new Set(mobileMenusWithCloseButton);

const getMenuContent = (menu: HomeownerAppMenuNamesValues | null) => {
  switch (menu) {
    case HomeownerAppMenuNames.ORIGINAL_IMAGE_MORE_ACTIONS:
      return <MoreActions />;
    case HomeownerAppMenuNames.PROFILE:
      return <ProfileSheet />;
    case HomeownerAppMenuNames.PROJECT_MORE_ACTIONS:
      return <ProjectMoreActions />;
    case HomeownerAppMenuNames.SIGN_IN:
      return <SignInSheet />;
    case HomeownerAppMenuNames.UPLOAD_PHOTO:
      return <UploadPhotoSheet />;
    case HomeownerAppMenuNames.WELCOME_BACK:
      return <WelcomeSheet />;
    case HomeownerAppMenuNames.WELCOME_TO:
      return <WelcomeSheet isSigningUp />;
    case HomeownerAppMenuNames.ADD_TO_PROJECT:
      return <AddToProject />;
    case HomeownerAppMenuNames.NEW_PROJECT:
      return <NewProject />;
    case HomeownerAppMenuNames.EDIT_PROJECT:
      return <EditProject />;
    case HomeownerAppMenuNames.ADD_SAVED_TO_PROJECT:
      return <AddSavedToProject />;
    case HomeownerAppMenuNames.ADD_IDEAS_TO_PROJECT:
      return <AddIdeasToProject />;
    case null:
      return null;
    default:
      return assertUnreachable(menu);
  }
};

const SheetMenu = () => {
  const { currentMenu, setCurrentMenu } = useHomeownerAppContext();
  const { setImageToSave } = useSaveToProjectContext();
  const history = useHistory();
  const [variant, setVariant] = useState<undefined | 'bottom'>(undefined);

  // Checking `!variant` below is required to prevent an infinite loop of setting state
  if (currentMenu && !variant && bottomVariantSheetMenusSet.has(currentMenu)) {
    setVariant('bottom');
  }

  const isClosable = useBreakpointValue({
    base: !!currentMenu && mobileMenusWithCloseButtonSet.has(currentMenu),
    tablet: true,
  });

  return (
    <Sheet.Container
      isClosable={isClosable}
      isOpen={!!currentMenu}
      onClose={() => {
        setCurrentMenu(null);
        setImageToSave(undefined);
        history.replace({
          pathname: location.pathname,
          state: undefined,
        });
      }}
      onCloseComplete={() => setVariant(undefined)}
      variant={variant}
    >
      {getMenuContent(currentMenu)}
    </Sheet.Container>
  );
};

export { SheetMenu };
